<template>
  <div class="article-list-page">
    <!-- 页面顶部横幅 -->
    <div class="page-banner">
      <div class="container banner-inner">
        <h1 class="banner-title">{{ pageTitle || $t('m.listTitle') }}</h1>
        <el-breadcrumb separator="/" class="banner-breadcrumb">
          <el-breadcrumb-item :to="{ path: '/' }">{{ $t('m.home') }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ pageTitle }}</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
    </div>

    <!-- 主体内容 -->
    <div class="container page-content" :class="{ 'has-sidebar': sidebar.length }">
      <!-- 左侧子分类导航（仅当传入 sidebar 时显示） -->
      <aside class="list-sidebar" v-if="sidebar.length">
        <ul class="sidebar-list">
        <li
          class="sidebar-item"
          v-if="showAll"
          :class="{ active: activeId == categoryId }"
          @click="selectCategory(categoryId)"
        >
          <span class="sidebar-dot"></span>{{ $t('m.all') }}
        </li>
          <li
            class="sidebar-item"
            v-for="(c, i) in sidebar"
            :key="i"
            :class="{ active: activeId == c.id }"
            @click="selectCategory(c.id)"
          >
            <span class="sidebar-dot"></span>{{ c.name }}
          </li>
        </ul>
      </aside>

      <!-- 右侧列表 -->
      <div class="list-main">
      <!-- 图文网格布局（新闻动态） -->
      <div class="list-grid" v-if="layout === 'grid' && list.length">
        <div
          class="grid-card"
          v-for="(item, index) in list"
          :key="index"
          @click="toDetail(item)"
        >
          <div class="grid-card-img-box">
            <img :src="fixUrl(item.url_image)" :alt="item.title_name" class="grid-card-img">
            <div class="grid-card-date" v-if="item.create_time">
              <span class="date-day">{{ dayOf(item.create_time) }}</span>
              <span class="date-month">{{ monthOf(item.create_time) }}</span>
            </div>
          </div>
          <div class="grid-card-info">
            <h3 class="grid-card-title text-ellipsis">{{ item.title_name }}</h3>
            <div class="grid-card-desc text-ellipsis-2">{{ summary(item.textcontent) }}</div>
            <div class="grid-card-more">
              <span>{{ $t('m.readMore') }}</span>
              <i class="el-icon-arrow-right"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- 文字列表布局（销售部门/英国分公司） -->
      <div class="list-text" v-if="layout === 'list' && list.length">
        <div
          class="text-item"
          v-for="(item, index) in list"
          :key="index"
          @click="toDetail(item)"
        >
          <div class="text-item-index">{{ String(index + 1).padStart(2, '0') }}</div>
          <div class="text-item-content">
            <h3 class="text-item-title text-ellipsis">{{ item.title_name }}</h3>
            <div class="text-item-desc text-ellipsis-2">{{ summary(item.textcontent) }}</div>
          </div>
          <div class="text-item-meta">
            <span class="text-item-date" v-if="item.create_time">{{ formatTime(item.create_time) }}</span>
            <span class="text-item-more">{{ $t('m.viewDetails') }} <i class="el-icon-arrow-right"></i></span>
          </div>
        </div>
      </div>

      <!-- 图片网格布局（资质证书） -->
      <div class="list-image" v-if="layout === 'image' && list.length">
        <div
          class="image-card"
          v-for="(item, index) in list"
          :key="index"
          @click="toDetail(item)"
        >
          <div class="image-card-img-box">
            <img :src="fixUrl(item.url_image)" :alt="item.title_name" class="image-card-img">
            <div class="image-card-hover">
              <i class="el-icon-zoom-in"></i>
              <span>{{ $t('m.viewLarge') }}</span>
            </div>
          </div>
          <div class="image-card-title text-ellipsis">{{ item.title_name }}</div>
        </div>
      </div>

      <!-- 空状态 -->
      <div class="empty-state" v-if="!list.length">
        <i class="el-icon-document"></i>
        <p>{{ loading ? $t('m.loading') : $t('m.noContent') }}</p>
      </div>

      <!-- 分页器 -->
      <div class="pagination-wrap" v-if="total > 0">
        <el-pagination
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-size="limit"
          layout="prev, pager, next, jumper"
          :total="total"
          :prev-text="$t('m.prevPage')"
          :next-text="$t('m.nextPage')"
        >
        </el-pagination>
        <span class="pagination-info">{{ $t('m.pageInfo', { total: total, current: currentPage, pages: totalPages }) }}</span>
      </div>
      </div><!-- /list-main -->
    </div>
  </div>
</template>

<script>
import url from "../request/base";
import { fixUrl as fixImgUrl, toPlainSummary } from "../utils/site";

export default {
  name: 'ArticleList',
  props: {
    // 分类ID
    categoryId: {
      type: [String, Number],
      default: 0
    },
    // 页面标题
    pageTitle: {
      type: String,
      default: ''
    },
    // 布局模式：grid(图文网格) / list(文字列表) / image(图片网格)
    layout: {
      type: String,
      default: 'grid'
    },
    // 详情页路径
    detailPath: {
      type: String,
      default: '/latestbroadcast/latestdetail'
    },
    // 左侧子分类列表 [{id, name}]，传入非空则显示左侧导航栏
    sidebar: {
      type: Array,
      default: () => []
    },
    // 是否显示左侧"全部"项；默认显示。
    // 部分页面（如"加入我们"）文章全挂在子分类下、顶级 pid 下无直接文章，
    // 保留"全部"会显示空列表，可传 false 隐藏，并默认选中第一项子分类
    showAll: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      imgurl: url.baseUrlimg,
      list: [],
      total: 0,
      currentPage: 1,
      limit: 12,
      loading: false,
      // 当前选中的分类 id（等于 categoryId 表示"全部"，等于某子分类 id 表示该子分类）
      activeId: 0
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.limit)
    }
  },
  watch: {
    categoryId: {
      handler() {
        // 切换顶级分类时重置选中态
        this.activeId = this.categoryId
        this.currentPage = 1
        if (this.showAll) {
          // 保留"全部"：直接查询顶级分类（含其下直接文章）
          this.getList()
        } else if (Array.isArray(this.sidebar) && this.sidebar.length) {
          // 禁用"全部"：默认选中第一项子分类，避免顶级 pid 下无文章导致空列表
          this.activeId = this.sidebar[0].id
          this.getList()
        }
      }
    },
    // 子分类异步到达后：禁用"全部"时自动落入第一项并加载
    sidebar: {
      handler(val) {
        if (!this.showAll && Array.isArray(val) && val.length && this.activeId === this.categoryId) {
          this.activeId = val[0].id
          this.currentPage = 1
          this.getList()
        }
      },
      immediate: true
    }
  },
  created() {
    this.activeId = this.categoryId
    // 禁用"全部"且子分类尚未到达时，先不发请求，等 sidebar watch 默认选中第一项后再加载，避免初始空列表闪烁
    if (this.showAll || !this.sidebar.length) {
      this.getList()
    }
  },
  methods: {
    // 切换左侧子分类
    selectCategory(id) {
      if (String(this.activeId) === String(id)) return
      this.activeId = id
      this.currentPage = 1
      this.getList()
    },
    // 拼接图片地址：后端若已返回完整 URL 则不再拼前缀
    fixUrl(path) {
      return fixImgUrl(this.imgurl, path)
    },
    // 列表简介取纯文本摘要
    // 用 v-html 渲染富文本再靠 CSS 截断，会出现未闭合标签撑破卡片布局
    summary(html) {
      return toPlainSummary(html, 100)
    },
    async getList() {
      // 后端 categoryDetails 的 id 走 param('id/d')，为 0 会被判为无效并返回空数据，
      // 因此空值 / 非数字 / 0 一律不发请求，直接展示空状态。
      // ⚠️ 0 在后端不是「查全部」——旧注释写反了，别再照它写逻辑。
      if (this.activeId === undefined || this.activeId === null ||
          this.activeId === '' || this.activeId === 'undefined' ||
          isNaN(Number(this.activeId)) || Number(this.activeId) === 0) {
        this.list = []
        this.total = 0
        this.loading = false
        return
      }

      this.loading = true
      try {
        const res = await this.$api.home.getcList({
          id: this.activeId,
          limit: this.limit,
          page: this.currentPage
        })
        if (res && res.data && res.data.list) {
          this.list = res.data.list
          this.total = res.data.total || 0
        } else {
          this.list = []
          this.total = 0
        }
      } catch (err) {
        this.list = []
        this.total = 0
        console.error('ArticleList getList error:', err)
      }
      this.loading = false
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.$nextTick(() => {
        this.getList()
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    },
    toDetail(item) {
      this.$router.push({
        path: this.detailPath,
        query: {
          id: item.id,
          name: item.title_name,
          pname: this.pageTitle,
          pid: this.activeId
        }
      })
    },
    // 时间归一化：兼容 10/13 位时间戳与后端返回的 "yyyy-MM-dd HH:mm:ss" 字符串
    // 旧实现直接 new Date(Number(x))，字符串型 create_time 会得到 NaN 而原样返回整串
    normalizeDate(timestamp) {
      if (!timestamp) return null
      const raw = String(timestamp)
      let date = null
      if (/^\d+$/.test(raw)) {
        date = new Date(raw.length === 10 ? Number(raw) * 1000 : Number(raw))
      } else {
        // 用 / 替换 -，兼容各端对 "yyyy-MM-dd HH:mm:ss" 的解析差异
        date = new Date(raw.replace(/-/g, '/'))
      }
      if (date && !isNaN(date.getTime())) {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return { year: String(y), month: m, day: d, text: `${y}-${m}-${d}` }
      }
      // 兜底：形如 2022-04-24 的字符串直接截取
      const s = raw.slice(0, 10)
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return { year: s.slice(0, 4), month: s.slice(5, 7), day: s.slice(8, 10), text: s }
      }
      return null
    },
    // 格式化日期（文字列表布局显示用）
    formatTime(timestamp) {
      const o = this.normalizeDate(timestamp)
      return o ? o.text : ''
    },
    // 图文网格卡片左上角日期块：日
    dayOf(timestamp) {
      const o = this.normalizeDate(timestamp)
      return o ? o.day : ''
    },
    // 图文网格卡片左上角日期块：月
    monthOf(timestamp) {
      const o = this.normalizeDate(timestamp)
      return o ? o.month + '月' : ''
    }
  }
}
</script>

<style scoped>
.article-list-page {
  min-width: 1200px;
  background-color: var(--color-bg-light);
}

/* ========== 页面横幅 ========== */
.page-banner {
  background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%);
  padding: 50px 0;
  position: relative;
  overflow: hidden;
}

.page-banner::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
}

.banner-inner {
  position: relative;
  z-index: 1;
}

.banner-title {
  font-size: 36px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-inverse);
  margin: 0 0 16px 0;
  letter-spacing: 4px;
}

.banner-breadcrumb {
  font-size: var(--font-size-sm);
}

.banner-breadcrumb :deep(.el-breadcrumb__inner) {
  color: rgba(255, 255, 255, 0.7);
}

.banner-breadcrumb :deep(.el-breadcrumb__item:last-child .el-breadcrumb__inner) {
  color: var(--color-text-inverse);
  font-weight: var(--font-weight-medium);
}

.banner-breadcrumb :deep(.el-breadcrumb__separator) {
  color: rgba(255, 255, 255, 0.5);
}

/* ========== 主体内容 ========== */
.page-content {
  padding: 50px 0;
  min-height: 500px;
}

/* 有左侧子分类时，主体改为左右两栏 */
.page-content.has-sidebar {
  display: flex;
  align-items: flex-start;
  gap: 30px;
}

/* ========== 左侧子分类导航 ========== */
.list-sidebar {
  width: 220px;
  flex-shrink: 0;
  background-color: var(--color-bg-white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.sidebar-list {
  list-style: none;
  margin: 0;
  padding: 10px 0;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 24px;
  font-size: var(--font-size-base);
  color: var(--color-text-regular);
  cursor: pointer;
  transition: all var(--transition-fast);
  border-left: 3px solid transparent;
}

.sidebar-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--color-border);
  flex-shrink: 0;
  transition: all var(--transition-fast);
}

.sidebar-item:hover {
  color: var(--color-accent);
  padding-left: 28px;
}

/* 选中态：墨绿色（与品牌辅助色一致） */
.sidebar-item.active {
  color: var(--color-accent);
  background-color: rgba(44, 58, 45, 0.08);
  font-weight: var(--font-weight-medium);
  border-left-color: var(--color-accent);
}

.sidebar-item.active .sidebar-dot {
  background-color: var(--color-accent);
}

/* 右侧列表容器 */
.list-main {
  flex: 1;
  min-width: 0;
}

/* ========== 图文网格布局 ========== */
.list-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.grid-card {
  background-color: var(--color-bg-white);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: all var(--transition-normal);
}

.grid-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-lg);
}

.grid-card-img-box {
  position: relative;
  width: 100%;
  height: 220px;
  overflow: hidden;
}

.grid-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-slow);
}

.grid-card:hover .grid-card-img {
  transform: scale(1.08);
}

.grid-card-date {
  position: absolute;
  top: 16px;
  left: 16px;
  min-width: 54px;
  background-color: var(--color-primary);
  color: var(--color-text-inverse);
  border-radius: var(--radius-md);
  padding: 8px 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.date-day {
  font-size: 20px;
  font-weight: var(--font-weight-bold);
}

.date-month {
  font-size: var(--font-size-xs);
  opacity: 0.8;
}

.grid-card-info {
  padding: 24px;
}

.grid-card-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  margin: 0 0 12px 0;
  transition: color var(--transition-fast);
}

.grid-card:hover .grid-card-title {
  color: var(--color-primary);
}

.grid-card-desc {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: var(--line-height-normal);
  height: 44px;
  margin-bottom: 16px;
}

.grid-card-desc :deep(img) {
  display: none;
}

.grid-card-more {
  display: flex;
  align-items: center;
  font-size: var(--font-size-sm);
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
}

.grid-card-more i {
  margin-left: 6px;
  transition: transform var(--transition-fast);
}

.grid-card:hover .grid-card-more i {
  transform: translateX(4px);
}

/* ========== 文字列表布局 ========== */
.list-text {
  background-color: var(--color-bg-white);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.text-item {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 24px 30px;
  border-bottom: 1px solid var(--color-border-light);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.text-item:last-child {
  border-bottom: none;
}

.text-item:hover {
  background-color: var(--color-primary-bg);
  padding-left: 36px;
}

.text-item-index {
  font-size: 32px;
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-bg);
  line-height: 1;
  flex-shrink: 0;
  width: 50px;
  transition: color var(--transition-fast);
}

.text-item:hover .text-item-index {
  color: var(--color-primary);
}

.text-item-content {
  flex: 1;
  min-width: 0;
}

.text-item-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  margin: 0 0 8px 0;
  transition: color var(--transition-fast);
}

.text-item:hover .text-item-title {
  color: var(--color-primary);
}

.text-item-desc {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: var(--line-height-normal);
  height: 40px;
}

.text-item-desc :deep(img) {
  display: none;
}

.text-item-meta {
  flex-shrink: 0;
  text-align: right;
}

.text-item-date {
  display: block;
  font-size: var(--font-size-sm);
  color: var(--color-text-placeholder);
  margin-bottom: 8px;
}

.text-item-more {
  display: inline-flex;
  align-items: center;
  font-size: var(--font-size-sm);
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
}

.text-item-more i {
  margin-left: 4px;
}

/* ========== 图片网格布局 ========== */
.list-image {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.image-card {
  cursor: pointer;
  transition: all var(--transition-normal);
}

.image-card:hover {
  transform: translateY(-4px);
}

.image-card-img-box {
  position: relative;
  width: 100%;
  padding-top: 130%;
  background-color: var(--color-bg-white);
  border-radius: var(--radius-md);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border-light);
  transition: all var(--transition-normal);
}

.image-card:hover .image-card-img-box {
  box-shadow: var(--shadow-md);
  border-color: var(--color-primary);
}

.image-card-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 16px;
  transition: transform var(--transition-normal);
}

.image-card:hover .image-card-img {
  transform: scale(1.05);
}

.image-card-hover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(44, 58, 45, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--color-text-inverse);
  opacity: 0;
  transition: opacity var(--transition-normal);
}

.image-card:hover .image-card-hover {
  opacity: 1;
}

.image-card-hover i {
  font-size: 36px;
  margin-bottom: 10px;
}

.image-card-hover span {
  font-size: var(--font-size-sm);
  letter-spacing: 2px;
}

.image-card-title {
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--color-text-regular);
  margin-top: 12px;
  transition: color var(--transition-fast);
}

.image-card:hover .image-card-title {
  color: var(--color-primary);
}

/* ========== 空状态 ========== */
.empty-state {
  text-align: center;
  padding: 80px 0;
  color: var(--color-text-placeholder);
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  display: block;
}

.empty-state p {
  font-size: var(--font-size-md);
  margin: 0;
}

/* ========== 分页器 ========== */
.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-top: 50px;
}

.pagination-info {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.pagination-wrap :deep(.el-pagination) {
  font-weight: normal;
}

.pagination-wrap :deep(.el-pagination.is-background .el-pager li:not(.disabled).active) {
  background-color: var(--color-primary);
}

.pagination-wrap :deep(.el-pagination.is-background .el-pager li:not(.disabled):hover) {
  color: var(--color-primary);
}

.pagination-wrap :deep(.el-pagination button:hover) {
  color: var(--color-primary);
}
</style>
